import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
    CalendarIcon,
    ClockIcon,
    EyeIcon,
    HeartIcon,
    TagIcon,
    UserIcon,
    PhotoIcon,
    ArrowRightIcon,
    SparklesIcon
} from '@heroicons/react/24/outline'
import { blogAPI } from '../../../services/api'
import LoadingSpinner from '../../../components/Common/LoadingSpinner'

const FeaturedBlogs = () => {
    const { data, isLoading, error } = useQuery({
        queryKey: ['featured-blogs'],
        queryFn: () => blogAPI.getFeaturedBlogs({ limit: 4 }),
        staleTime: 10 * 60 * 1000, // 10 minutes
    })

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    }

    const truncateText = (text, maxLength) => {
        if (text.length <= maxLength) return text
        return text.substr(0, maxLength) + '...'
    }

    if (isLoading) {
        return (
            <div className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <LoadingSpinner />
                    </div>
                </div>
            </div>
        )
    }

    if (error || !data?.data?.blogs?.length) {
        return null // Don't show section if no blogs
    }

    const blogs = data.data.blogs

    return (
        <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center space-x-2 bg-primary-50 px-4 py-1.5 rounded-full mb-6 border border-primary-100">
                            <SparklesIcon className="w-5 h-5 text-primary-600" />
                            <span className="text-sm font-bold text-primary-700 uppercase tracking-wider">Expert Articles</span>
                        </div>
                        <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6 leading-tight tracking-tight">
                            Latest <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-600">Education Insights</span>
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto font-medium leading-relaxed">
                            Stay updated with the latest education news, career guidance, and industry insights from our expert team.
                        </p>
                    </motion.div>
                </div>

                {/* Blogs Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                    {blogs.map((blog, index) => (
                        <motion.article
                            key={blog._id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group"
                        >
                            {/* Featured Image */}
                            <div className="aspect-video bg-gray-200 relative overflow-hidden">
                                {blog.featured_image ? (
                                    <img
                                        src={blog.featured_image}
                                        alt={blog.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <PhotoIcon className="h-12 w-12 text-gray-400" />
                                    </div>
                                )}
                                <div className="absolute top-4 left-4">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/90 text-blue-800">
                                        {blog.category}
                                    </span>
                                </div>
                            </div>

                            <div className="p-6">
                                {/* Title */}
                                <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
                                    <Link to={`/blogs/${blog.slug}`}>
                                        {blog.title}
                                    </Link>
                                </h3>

                                {/* Excerpt */}
                                <p className="text-gray-600 mb-4 line-clamp-3">
                                    {truncateText(blog.excerpt, 120)}
                                </p>

                                {/* Tags */}
                                {blog.tags && blog.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {blog.tags.slice(0, 2).map((tag, tagIndex) => (
                                            <span
                                                key={tagIndex}
                                                className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                                            >
                                                <TagIcon className="h-3 w-3 mr-1" />
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Meta Information */}
                                <div className="flex items-center justify-between text-sm text-gray-500">
                                    <div className="flex items-center space-x-4">
                                        <div className="flex items-center">
                                            <UserIcon className="h-4 w-4 mr-1" />
                                            {blog.created_by?.name || 'Admin'}
                                        </div>
                                        <div className="flex items-center">
                                            <CalendarIcon className="h-4 w-4 mr-1" />
                                            {formatDate(blog.published_at || blog.createdAt)}
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <div className="flex items-center">
                                            <EyeIcon className="h-4 w-4 mr-1" />
                                            {blog.views || 0}
                                        </div>
                                    </div>
                                </div>

                                {/* Read More */}
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <Link
                                        to={`/blogs/${blog.slug}`}
                                        className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm group-hover:translate-x-1 transition-transform"
                                    >
                                        Read More
                                        <ArrowRightIcon className="h-4 w-4 ml-1" />
                                    </Link>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>

                {/* View All Button */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="text-center"
                >
                    <Link
                        to="/blogs"
                        className="btn-outline btn-lg inline-flex items-center space-x-2"
                    >
                        View All Blogs
                        <ArrowRightIcon className="ml-2 h-5 w-5" />
                    </Link>
                </motion.div>
            </div>
        </section>
    )
}

export default FeaturedBlogs
